import type { ReportMode } from '../components/ReportPeriodSwitcher';

import { Helmet } from '@dr.pogodin/react-helmet';
import { useApiRead } from 'api-read-hook';
import {
  addMonths,
  addWeeks,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  formatISO,
  isSameDay,
  isSameMonth,
  isSameWeek,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Api, useApiCallable } from '../../../api';
import { ApiError } from '../../../api/hub-api-request';
import {
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
} from '../../../components';
import Text from '../../../components/Text';
import { Color } from '../../../constants';
import { useCompany } from '../../../contexts/ClientDataContext';
import { useMyUserClient } from '../../../hooks';
import { Model } from '../../../models';
import { makeQueryString } from '../../../util';
import {
  MaintenanceTicketsTabs,
  ReportApproval,
  ReportPeriodSwitcher,
  ReportSection,
  ReportStat,
  ReportSummaryRow,
} from '../components';
import { formatReportMinutes } from '../util';

/** Surface whatever message the API returned (hydra:description / detail). */
function alertApiError(err: unknown) {
  const data = (err instanceof ApiError ? err.response.data : {}) as Record<
    string,
    unknown
  >;
  const pickString = (key: string) =>
    typeof data[key] === 'string' ? (data[key] as string) : null;
  alert(
    pickString('hydra:description') ??
      pickString('detail') ??
      pickString('hydra:title') ??
      'Something went wrong. Please try again.'
  );
}

export default function ReportPage() {
  const { companySlug, reportMode } = useParams<{
    companySlug: string;
    reportMode: string;
  }>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const company = useCompany(companySlug!);
  const myUser = useMyUserClient();
  const callApi = useApiCallable();

  const mode: ReportMode = reportMode === 'weekly' ? 'week' : 'month';
  const isAgency = !!myUser.agencyRole;

  const today = new Date();
  const searchParams = new URLSearchParams(search);
  const currentPeriodStart =
    mode === 'month'
      ? startOfMonth(today)
      : startOfWeek(today, { weekStartsOn: 1 });

  const apiApprovals = Api.TicketReportApproval.useReadCollectionCompanySub(
    company.id
  );
  const approvals = apiApprovals.data?.['hydra:member'] ?? [];

  // The approved period starts for this mode, oldest first.  A company user
  // can only ever open approved periods, so these drive the default period,
  // the dropdown and the arrows.
  const approvedStarts = (
    mode === 'month'
      ? approvals
          .filter((a) => a.month != null)
          .map((a) => parseISO(a.month as string))
      : approvals
          .filter((a) => a.week != null)
          .map((a) => parseISO(a.week as string))
  ).sort((a, b) => a.getTime() - b.getTime());
  const latestApproved = approvedStarts[approvedStarts.length - 1];

  const queryPeriod = searchParams.get(mode === 'month' ? 'month' : 'week');
  const parsedPeriod = queryPeriod ? parseISO(queryPeriod) : null;
  const parsedValid =
    parsedPeriod != null && !Number.isNaN(parsedPeriod.getTime());

  // A hand-edited ?month=/?week= value may be unparseable or land mid-period;
  // pin the start to the boundary, which is also what the approval lookup and
  // backend expect.  With no explicit period, agency staff start at the
  // current period and a company user lands on their most recent approval.
  const periodStart = parsedValid
    ? mode === 'month'
      ? startOfMonth(parsedPeriod)
      : startOfWeek(parsedPeriod, { weekStartsOn: 1 })
    : !isAgency && latestApproved
    ? latestApproved
    : currentPeriodStart;
  const period: Model.Ticket.ReportPeriod = {
    start: periodStart,
    end:
      mode === 'month' ? addMonths(periodStart, 1) : addWeeks(periodStart, 1),
  };

  const reportParams =
    mode === 'month'
      ? {
          company: company['@id'],
          month: formatISO(periodStart, { representation: 'date' }),
        }
      : {
          company: company['@id'],
          week: formatISO(periodStart, { representation: 'date' }),
        };
  // Hold the report request for a company user until their approvals are
  // known, so the first render doesn't fetch an unapproved period and throw a
  // stale 403 at the error branch before their period has settled.
  const apiReport = useApiRead<Api.TicketReport.V>(
    !isAgency && !apiApprovals.data
      ? null
      : `/tickets/report${makeQueryString(reportParams)}`
  );

  const [busy, setBusy] = React.useState(false);

  const navigateTo = (next: Date) => {
    const params = new URLSearchParams(search);
    params.set(
      mode === 'month' ? 'month' : 'week',
      formatISO(next, { representation: 'date' })
    );
    navigate(`${pathname}?${params.toString()}`);
  };

  const approvedIndex = isAgency
    ? -1
    : approvedStarts.findIndex((d) => isSameDay(d, periodStart));

  const isCurrentPeriod =
    mode === 'month'
      ? isSameMonth(periodStart, today)
      : isSameWeek(periodStart, today, { weekStartsOn: 1 });

  // Agency staff roam the calendar freely; a company user only moves between
  // the periods that have been approved for them.
  const canGoNext = isAgency
    ? !isCurrentPeriod
    : approvedIndex >= 0 && approvedIndex < approvedStarts.length - 1;
  const canGoPrev = isAgency || approvedIndex > 0;

  const handleNavigate = (delta: -1 | 1) => {
    if (isAgency) {
      navigateTo(
        mode === 'month'
          ? addMonths(periodStart, delta)
          : addWeeks(periodStart, delta)
      );
      return;
    }
    const target = approvedStarts[approvedIndex + delta];
    if (target) navigateTo(target);
  };

  const periods = isAgency
    ? mode === 'month'
      ? eachMonthOfInterval({
          start: startOfMonth(subMonths(today, 11)),
          end: startOfMonth(today),
        }).reverse()
      : eachWeekOfInterval(
          {
            start: startOfWeek(subWeeks(today, 11), { weekStartsOn: 1 }),
            end: startOfWeek(today, { weekStartsOn: 1 }),
          },
          { weekStartsOn: 1 }
        ).reverse()
    : [...approvedStarts].reverse();

  const approval = approvals.find((a) => {
    const periodIso = mode === 'month' ? a.month : a.week;
    return periodIso != null && isSameDay(parseISO(periodIso), periodStart);
  });

  async function approve() {
    setBusy(true);
    try {
      await callApi('/ticket-report-approvals', {
        method: 'POST',
        jsonBody: {
          company: company['@id'],
          [mode]: formatISO(periodStart, { representation: 'date' }),
          isTimeLoggedVisible: true,
        },
      });
      apiApprovals.invalidate();
    } catch (err: unknown) {
      alertApiError(err);
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!approval) return;
    setBusy(true);
    try {
      await callApi(`/ticket-report-approvals/${approval.id}`, {
        method: 'DELETE',
      });
      apiApprovals.invalidate();
    } catch (err: unknown) {
      alertApiError(err);
    } finally {
      setBusy(false);
    }
  }

  if (apiReport.error) {
    // A company user only hits 403 when a period has not been approved for
    // them yet; any other failure is a real error worth showing.
    const isUnapprovedPeriod =
      apiReport.error instanceof ApiError &&
      apiReport.error.response?.status === 403 &&
      !myUser.agencyRole;
    if (isUnapprovedPeriod) {
      return (
        <PageWrap background={Color.neutral5} noPadding limitWidth="large">
          <Stack padH={1.5} padV={1.5}>
            <MaintenanceTicketsTabs
              companySlug={companySlug!}
              selected={mode === 'month' ? 'monthly' : 'weekly'}
              approvals={approvals}
            />
            <EmptyState
              title="Not visible yet"
              subtitle="This period has not been approved for your company yet."
            />
          </Stack>
        </PageWrap>
      );
    }
    return <PageError {...apiReport} />;
  }
  if (apiApprovals.error) return <PageError {...apiApprovals} />;
  if (!apiReport.data || !apiApprovals.data) return <PageLoading />;

  const tickets = apiReport.data.tickets;
  const companyActivities = apiReport.data.companyActivities;
  const opened = tickets
    .filter((ticket) => Model.Ticket.isOpenedInPeriod(ticket, period))
    .sort((a, b) => b.ticket.number - a.ticket.number);
  const updated = tickets
    .filter((ticket) => Model.Ticket.isUpdatedInPeriod(ticket, period))
    .sort((a, b) => b.ticket.number - a.ticket.number);
  const openedMinutes = opened.reduce(
    (sum, ticket) => sum + Model.Ticket.getMinutesInPeriod(ticket),
    0
  );
  const updatedMinutes = updated.reduce(
    (sum, ticket) => sum + Model.Ticket.getMinutesInPeriod(ticket),
    0
  );
  const otherMinutes = companyActivities.reduce(
    (sum, activity) => sum + activity.minutes,
    0
  );

  const renderRows = (rows: Model.Ticket.ReportTicket[]) =>
    rows.map((ticket, index) => (
      <React.Fragment key={ticket['@id']}>
        {index > 0 && <html.div style={styles.rule} />}
        <ReportSummaryRow
          href={`/company/${companySlug}/tickets/${ticket.ticket.number}`}
          number={ticket.ticket.number}
          title={ticket.ticket.title}
          status={Model.Ticket.getReportStatus(ticket)}
          minutes={Model.Ticket.getMinutesInPeriod(ticket)}
        />
      </React.Fragment>
    ));

  return (
    <PageWrap background={Color.neutral5} noPadding limitWidth="large">
      <Helmet>
        <title>
          {`${mode === 'month' ? 'Monthly' : 'Weekly'} Summary | ${
            company.name
          }`}
        </title>
      </Helmet>

      <Stack padH={1.5} padV={1.5} gap={1.5}>
        <MaintenanceTicketsTabs
          companySlug={companySlug!}
          selected={mode === 'month' ? 'monthly' : 'weekly'}
          approvals={approvals}
        />

        <html.div style={styles.monthBar}>
          <ReportPeriodSwitcher
            mode={mode}
            period={periodStart}
            periods={periods}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            hint={isCurrentPeriod ? `· ${mode} to date` : null}
            onNavigate={handleNavigate}
            onSelect={navigateTo}
          />
          {myUser.agencyRole && (
            <ReportApproval
              approved={approval != null}
              canApprove={myUser.agencyRole === 'owner'}
              periodName={
                mode === 'month'
                  ? format(periodStart, 'MMMM')
                  : format(periodStart, 'd MMM')
              }
              busy={busy}
              onApprove={approve}
              onRevoke={revoke}
            />
          )}
        </html.div>

        <html.div style={styles.summaryCard}>
          <html.div style={styles.summaryRow}>
            <ReportStat value={String(opened.length)} label="Opened" />
            <html.div style={styles.ruleVertical} />
            <ReportStat value={String(updated.length)} label="Updated" />
          </html.div>
          <html.div style={styles.ruleVerticalRight} />
          <html.div style={styles.ruleHorizontal} />
          <html.div style={styles.summaryTimeRow}>
            <ReportStat
              value={formatReportMinutes(
                openedMinutes + updatedMinutes + otherMinutes
              )}
              label="Time logged"
            />
          </html.div>
        </html.div>

        <ReportSection
          title={mode === 'month' ? 'Opened this month' : 'Opened this week'}
          count={opened.length}
          subtotal={formatReportMinutes(openedMinutes)}
        >
          {renderRows(opened)}
        </ReportSection>

        <ReportSection
          title={mode === 'month' ? 'Updated this month' : 'Updated this week'}
          count={updated.length}
          subtotal={formatReportMinutes(updatedMinutes)}
        >
          {renderRows(updated)}
        </ReportSection>

        {companyActivities.length > 0 && (
          <ReportSection
            title={
              mode === 'month'
                ? 'Other work this month'
                : 'Other work this week'
            }
            count={companyActivities.length}
            subtotal={formatReportMinutes(otherMinutes)}
            showCount={false}
          >
            {companyActivities.map((activity, index) => (
              <React.Fragment key={activity['@id']}>
                {index > 0 && <html.div style={styles.rule} />}
                <html.div style={styles.activityRow}>
                  <Text typeface="body.regular" color="neutral100">
                    {activity.category
                      ? Model.WorkDoneCategory.format(activity.category)
                      : 'Uncategorized'}
                  </Text>
                  <Text
                    typeface="body.small.medium"
                    color={activity.minutes > 0 ? 'neutral100' : 'neutral40'}
                  >
                    {formatReportMinutes(activity.minutes)}
                  </Text>
                </html.div>
              </React.Fragment>
            ))}
          </ReportSection>
        )}
      </Stack>
    </PageWrap>
  );
}

const styles = css.create({
  monthBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 8,
  },
  summaryCard: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 12,
    overflow: 'hidden',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  summaryRow: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 2,
    minWidth: 0,
    '@media (max-width: 768px)': {
      flexGrow: 1,
    },
  },
  summaryTimeRow: {
    display: 'flex',
    flexGrow: 1,
    minWidth: 0,
  },
  rule: {
    height: 1,
    backgroundColor: color.neutral10,
  },
  activityRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingBlock: 12,
    paddingInline: 16,
  },
  ruleVertical: {
    width: 1,
    backgroundColor: color.neutral10,
  },
  ruleVerticalRight: {
    width: 1,
    backgroundColor: color.neutral10,
    display: 'block',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  ruleHorizontal: {
    height: 1,
    backgroundColor: color.neutral10,
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
    },
  },
});
